import { Box, Card, CardContent, Skeleton, Typography } from "@mui/material";
import { useMedia } from "react-use";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import theme from "../../App/Styles/theme";
import { useCampaignProfileReportContext } from "../../CampaignProfile/State/CampaignReportContext";
import { DiagonalXAxisTick } from "../Components/DiagonalXAxisTick";
import { getThemedColorCode } from "../Helpers/ThemedColorCodes";

const CustomToolTip = (props: any) => {
  const { active, payload } = props;

  if (active && payload?.length) {
    const dayTotal = payload.reduce((acc: number, curr: { value: number }) => acc + Number(curr.value), 0);
    return (
      <Card>
        <CardContent
          sx={{
            ".MuiTypography-root": { textAlign: "center", lineHeight: 1 },
            "&:last-child": { py: 1 },
          }}
        >
          <Typography variant="h4" component="p" fontWeight={700} data-test={`daily-total`}>
            {dayTotal}
          </Typography>
          <Typography fontWeight={400} color={(theme) => theme.palette.text.secondary} sx={{ mb: 0, mt: 1 }}>
            {payload[0].payload.dateAdded}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return null;
};

export const CampaignProfileCandidatesCapturedReport = () => {
  const {
    candidatesAddedState: {
      getters: { reportData, isReportEnabled, isLoading },
    },
  } = useCampaignProfileReportContext();

  const isMobile = useMedia("(max-width: 600px)");
  const isTablet = useMedia("(max-width: 960px)");

  if (!isReportEnabled) {
    return null;
  }

  const campaignData = reportData?.campaignsInReport.find(() => true);
  const aspect = isMobile ? 1.5 : isTablet ? 2 : 4;
  const dotRadius = isMobile ? 6 : 8;

  return (
    <Box
      sx={{
        display: "flex",
        pb: 2,
        gap: 2,
        flexDirection: "column",
        flexGrow: 1,
        ".yAxis tspan": { color: theme.palette.text.disabled },
      }}
    >
      <Typography variant="h5" data-test="candidates-by-campaign-report" color={"secondary"}>
        Candidate Captures Per Day
      </Typography>
      <ResponsiveContainer aspect={aspect} width="100%" debounce={300}>
        {isLoading ? (
          <Skeleton height={"100%"} variant="rectangular" style={{ maxWidth: "100%" }} />
        ) : (
          <AreaChart
            data={reportData?.tableData}
            margin={{
              top: 20,
              right: 20,
              left: 10,
              bottom: 60,
            }}
          >
            <defs>
              <linearGradient id="area-gradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3} />
                <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#eeeded" vertical={false} />
            <XAxis
              dataKey="dateAdded"
              axisLine={false}
              tickFormatter={(value: any) => new Date(value).toLocaleDateString()}
              tick={<DiagonalXAxisTick />}
              tickLine={false}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              ticks={campaignData ? undefined : [0, 50, 100, 150, 200]}
              type="number"
              domain={["auto", "dataMax"]}
            />
            <Tooltip
              content={<CustomToolTip />}
              cursor={{ stroke: theme.palette.primary.main, strokeDasharray: 5 }}
            />
            <Area
              type="monotone"
              key={campaignData?.name.replace(/\s/g, "-")}
              dataKey={campaignData?.name || "No Form"}
              id={`candidates-${campaignData?.name.replace(/\s/g, "-")}`}
              stroke={getThemedColorCode(0)}
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#area-gradient)"
              activeDot={{ r: dotRadius }}
            />
          </AreaChart>
        )}
      </ResponsiveContainer>
    </Box>
  );
};
